import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState, store } from "./store";
import { isExpired, decodeToken } from "react-jwt";
import { useSearchParams } from "react-router-dom";

// Define types for token and authentication status
type Token = string | null;

interface AuthStatus {
  isAuthenticated: boolean;
  token: Token;
  isLoading: boolean;
  isMember: boolean;
  isConfirmed: boolean;
  updateToken: (newToken: Token) => void;
}

// Custom hook for managing JWT token
export const useAuthToken = (): AuthStatus => {
  // Initialize state for token and authentication status
  const dispatch = useDispatch();
  const storedToken = useSelector((state: AppState) => state.token);
  const [token, setToken] = useState<Token>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isMember, setIsMember] = useState<boolean>(false);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  const saveToken = (token: string) => {
    dispatch({
      type: "UPDATE_TOKEN",
      payload: token,
    });
  };

  async function getTokenValid(token: string): Promise<boolean | undefined> {
    const apiEndpoint =
       "/api/token_valid?token=" + token;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: headers,
      });

      if (response.ok) {
        const responseData = await response.json();
        setIsMember(responseData.member);
        setIsConfirmed(responseData.confirmed);
        return true;
      }
    } catch (error) {
      return false;
    }

    return false;
  }

  // Load token from localStorage on component mount
  useEffect(() => {
    if (storedToken) {
      getTokenValid(storedToken).then((value) => {
        if (value) {
          setToken(storedToken);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          saveToken("");
        }

        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [storedToken]);

  // Function to update token and authentication status
  const updateToken = (newToken: Token) => {
    if (newToken) {
      saveToken(newToken);
      setToken(newToken);
    } else {
      saveToken("");
      setToken(null);
    }
  };

  return {
    isAuthenticated,
    token,
    isLoading,
    isMember,
    isConfirmed,
    updateToken,
  };
};
